/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import base32Encode from 'base32-encode';
import { AttachedClient } from '../models/Account';
import { navigate, NavigateFn, NavigateOptions } from '@reach/router';
import { SEND_TAB_ENTRYPOINTS } from '../constants';
import { Constants } from './constants';

// Various utilities that don't fit in a standalone lib

/**
 * Recursively merge an object
 **/
export function deepMerge(
  target: Hash<any>,
  ...sources: Hash<any>[]
): Hash<any> {
  if (!sources.length) {
    return target;
  }

  const source = sources.shift();

  for (const key in source) {
    const value = source[key];
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      if (!target[key]) {
        Object.assign(target, { [key]: {} });
      }

      deepMerge(target[key], value);
    } else {
      Object.assign(target, { [key]: value });
    }
  }

  return deepMerge(target, ...sources);
}

export function getSearchParams(paramKeys: string[], locationHref: string) {
  const { searchParams } = new URL(locationHref);
  const params: Record<string, string | null> = {};

  for (const paramKey of paramKeys) {
    params[paramKey] = searchParams.get(paramKey);
  }
  return params;
}

/**
 * Convert a search string to its object representation, one entry
 * per query parameter. Assumes the string is a search string and
 * not a full URL without a search string.
 */
export function searchParams(str = '', allowedFields?: string[]) {
  // ditch everything before the ? and from # to the end
  const search = str.replace(/(^.*\?|#.*$)/g, '').trim();
  if (!search) {
    return {};
  }

  return splitEncodedParams(search, allowedFields!);
}

/**
 * Return the value of a single query parameter in the string
 */
export function searchParam(name: string, str?: string) {
  const params = searchParams(str);
  return params[name];
}

/**
 * Convert a URI encoded string to its object representation.
 *
 * `&` is the expected delimiter between parameters.
 * `=` is the delimiter between a key and a value.
 */
export function splitEncodedParams(
  str = '',
  allowedFields?: string[]
): Record<string, string> {
  const pairs = str.split('&');
  const terms: { [key: string]: string } = {};

  pairs.forEach((pair) => {
    const [key, value] = pair.split('=');
    terms[key] = decodeURIComponent(value).trim();
  });

  if (!allowedFields) {
    return terms;
  }

  return Object.keys(terms)
    .filter((key) => allowedFields.indexOf(key) >= 0)
    .reduce((newObj, key) => Object.assign(newObj, { [key]: terms[key] }), {});
}

export function isMobileDevice(
  client?: Pick<AttachedClient, 'deviceType' | 'name'>
) {
  if (client) {
    return (
      client.deviceType === 'mobile' ||
      (client.name || '').toLowerCase().includes('ipad')
    );
  }
  return /mobi/i.test(navigator.userAgent);
}

// User-agent based check for a phone OR tablet, for promos that target the
// mobile app on both. Phones carry "Mobi"/"Mobile"; Android tablets omit
// "Mobile" but keep "Android"/"Tablet"; iPadOS 13+ Safari reports a desktop
// macOS UA, so it is detected via the MacIntel + touch-points combination.
export function isMobileOrTabletDevice() {
  const ua = navigator.userAgent;
  if (/mobi|android|tablet|ipad/i.test(ua)) {
    return true;
  }
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

// Crockford base32 Regex. Case insensitive and excludes I, L, O, U
const B32_STRING = /^[0-9A-HJ-KM-NP-TV-Z]+$/i;
export function isBase32Crockford(value: string) {
  return B32_STRING.test(value);
}

export function formatRecoveryKey(recoveryKeyBuffer: ArrayBufferLike) {
  return base32Encode(recoveryKeyBuffer, 'Crockford').match(/.{4}/g)!.join(' ');
}

/**
 * Ensures a given callback is called at most once per invocation with a given key.
 * For example:
 *  foo('bar', () => console.log(1));
 *  foo('bar', () => console.log(2));
 * Would print: 1
 */
let calls = new Set<string>();
export function once(key: string, callback: () => void) {
  if (calls.has(key)) {
    return;
  }
  calls.add(key);
  callback();
}
export function resetOnce() {
  calls = new Set<string>();
}

/**
 * Constructs a URL with UTM parameters appended to the query string.
 *
 * @param pathname - The base URL path.
 * @param utmMedium - The medium through which the link is being shared.
 * @param utmSource - The source of the traffic.
 * @param utmTerm - The search term or keyword associated with the campaign.
 * @param utmContent - The specific content or product that the link is associated with.
 * @param utmCampaign - The name of the marketing campaign.
 * @returns {string} - The constructed URL with UTM parameters.
 */
export const constructHrefWithUtm = (
  pathname: string,
  utmMedium?: 'mozilla-websites' | 'product-partnership' | 'referral',
  utmSource?: 'moz-account',
  utmTerm?: 'bento' | 'sidebar' | 'settings',
  utmContent?:
    | 'fx-desktop'
    | 'fx-mobile'
    | 'monitor'
    | 'monitor-free'
    | 'relay'
    | 'vpn'
    | 'get-free-scan-global'
    | 'get-year-round-protection-us',
  utmCampaign?: 'permanent' | 'settings-promo' | 'connect-device'
) => {
  const pairs: [string, string | undefined | null][] = [
    ['utm_source', utmSource],
    ['utm_medium', utmMedium],
    ['utm_term', utmTerm],
    ['utm_content', utmContent],
    ['utm_campaign', utmCampaign],
  ];

  const query = pairs
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return query ? `${pathname}?${query}` : pathname;
};

/**
 * Formats an authenticator secret into a human-readable format.
 * The secret is converted to uppercase and split into groups of 4 characters.
 *
 * @param {string} secret - The authenticator secret to format.
 * @returns {string} - The formatted secret.
 */
export const formatSecret = (secret: string) => {
  return secret.toUpperCase().match(/.{4}/g)!.join(' ');
};

/**
 * A helper function containing the shared logic of both the
 * useNavigateWithQuery hook and the navigateWithQuery function
 */
export function navigateWithQueryHelper(
  location: Location,
  navigate: NavigateFn,
  to: string,
  options?: NavigateOptions<{}>,
  includeHash: boolean = true
): Promise<void> {
  const [destinationPath, destinationHash = ''] = to.split('#');
  let path = destinationPath;

  if (
    !destinationPath.includes('?') &&
    location.search &&
    location.search !== '?'
  ) {
    path = `${path}${location.search}`;
  }

  if (to.includes('#')) {
    path = `${path}#${destinationHash}`;
  } else if (includeHash && location.hash) {
    path = `${path}${location.hash}`;
  }

  return options ? navigate(path, options) : navigate(path);
}

/**
 * **NOTE:** This should only be used outside of a router context.
 * Prefer using useNavigateWithQuery when possible.
 *
 * The non-hook version of useNavigateWithQuery
 */
export function navigateWithQuery(
  to: string,
  options?: NavigateOptions<{}>,
  includeHash: boolean = true
): Promise<void> {
  return navigateWithQueryHelper(
    window.location,
    navigate,
    to,
    options,
    includeHash
  );
}

/**
 * Simplifies user agent OS names to generic popular names.
 * Ported from fxa-content-server/app/scripts/lib/user-agent.js:toGenericOSName
 */
export function toGenericOSName(os: string): string {
  if (/^Windows/.test(os)) {
    return 'Windows';
  }
  if (/^Android/.test(os)) {
    return 'Android';
  }
  if (/^Mac OS/.test(os)) {
    return 'macOS';
  }
  if (/^iOS/.test(os)) {
    return 'iOS';
  }
  if (/^(Ubuntu|Linux|Fedora|Red Hat|Debian)/.test(os)) {
    return 'Linux';
  }
  return os || 'Unknown';
}

/**
 * Intended as a loose guard for CMS-provided URLs before they're assigned to
 * `<img src>`/`href` or similar sinks. Rejects `javascript:`, `data:`, `http:`,
 * and malformed input.
 */
export function isValidCmsUrl(value: string | null | undefined) {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Default fallback message used across pairing pages when the error is not an Error instance. */
export const DEFAULT_PAIRING_ERROR = 'An error occurred during pairing';

/** Extract a human-readable error message from an unknown thrown value. */
export function getPairingErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : DEFAULT_PAIRING_ERROR;
}

/** Whether the given entrypoint originated from a Firefox "Send Tab" UI. */
export function isSendTabEntrypoint(
  entrypoint: string | null | undefined
): entrypoint is string {
  return !!entrypoint && SEND_TAB_ENTRYPOINTS.has(entrypoint);
}

/** URL for the pairing QR: the send-tab Mozilla shortlink for send-tab entrypoints, else the generic Mozilla shortlink. */
export function buildPairingDownloadUrl(entrypoint?: string | null): string {
  return isSendTabEntrypoint(entrypoint)
    ? Constants.DOWNLOAD_LINK_PAIRING_QR_SEND_TAB
    : Constants.DOWNLOAD_LINK_PAIRING_QR_DEFAULT;
}
